/**
 * src/api/mutationQueue.ts — Offline-tolerant queue for customer mutations.
 *
 * Generalization of scanQueue.ts (which still lives separately and owns
 * driver-scan retries). MutationQueue is the same idea applied to customer-
 * facing writes (schedule pickup, schedule return, cancel order, update
 * profile, etc.) — write goes into localStorage immediately, the executor
 * tries to reach the server, and we retry on background with exponential
 * backoff if the network is flaky. ENGINEERING.md §1 / ROADMAP §6.2.
 *
 * Consumer pattern:
 *
 *   1. registerMutationExecutor('schedule_pickup', async (payload) => {
 *        await schedulePickup(payload)
 *      })
 *
 *   2. await enqueueMutation({ type: 'schedule_pickup', payload: {...} })
 *      // Resolves AFTER the first successful execution OR throws if the
 *      // executor reports a permanent error (4xx that the user should see).
 *      // Network errors / 5xx → kept in queue, resolved on a future retry.
 *
 *   3. Components subscribe to size + state via subscribeToMutationQueue
 *      (powering a "Syncing N changes…" indicator).
 *
 * Why not TanStack Query mutations: those work great when the network is up
 * and don't survive page reload. We need the second guarantee — a customer
 * cancelling an order on flaky LTE who closes the tab before the request
 * settles still expects the cancel to land.
 */

const STORAGE_KEY = 'attic_mutation_queue_v1';
const FAILED_KEY = 'attic_mutation_queue_failed_v1';

/**
 * Backoff schedule. Each retry waits min(BASE * 2^attempts, MAX) plus jitter.
 * After MAX_ATTEMPTS the entry moves to the failed bucket so the consumer
 * can surface a "couldn't sync" banner with a manual retry button.
 */
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 60_000;
const RETRY_JITTER_MS = 500;
const MAX_ATTEMPTS = 8;

export interface QueuedMutation<P = unknown> {
  /** Unique id so multiple queued entries for the same operation don't collide. */
  id: string;
  /** Discriminator — looks up the registered executor. */
  type: string;
  /** Serializable payload for the executor. */
  payload: P;
  /** ISO timestamp the entry was queued (client clock). */
  queuedAt: string;
  /** How many times the executor has been attempted. */
  attempts: number;
  /** Soonest the next retry may run (epoch ms). null = run on next tick. */
  nextAttemptAt: number | null;
}

export interface FailedMutation<P = unknown> extends QueuedMutation<P> {
  /** Error message from the final attempt. */
  error: string;
  /** ISO timestamp of the final failure. */
  failedAt: string;
}

export interface MutationQueueSnapshot {
  pending: QueuedMutation[];
  failed: FailedMutation[];
}

type Listener = (snapshot: MutationQueueSnapshot) => void;

/**
 * Errors that the executor *expects* are unrecoverable — bad input, 4xx
 * validation, ownership violations. Throwing one of these moves the entry
 * straight to the failed bucket without retrying. Any other thrown error
 * is treated as a transient/network failure and retried.
 */
export class PermanentMutationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermanentMutationError';
  }
}

// -- Executor registry ------------------------------------------------------

type Executor<P = unknown> = (payload: P) => Promise<unknown>;
const executors = new Map<string, Executor>();

export function registerMutationExecutor<P>(type: string, fn: Executor<P>): void {
  executors.set(type, fn as Executor);
}

/** Test-only — clear all registered executors. Not exported in production. */
export function _resetMutationExecutorsForTests(): void {
  executors.clear();
}

// -- Storage access ---------------------------------------------------------

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full / disabled — silent fail. The queue will still live
    // in-memory for this session; we just lose the offline guarantee.
  }
}

function loadPending(): QueuedMutation[] { return readJson(STORAGE_KEY, [] as QueuedMutation[]); }
function loadFailed(): FailedMutation[] { return readJson(FAILED_KEY, [] as FailedMutation[]); }
function savePending(q: QueuedMutation[]): void { writeJson(STORAGE_KEY, q); }
function saveFailed(q: FailedMutation[]): void { writeJson(FAILED_KEY, q); }

// -- Subscription -----------------------------------------------------------

const listeners = new Set<Listener>();
function notify(): void {
  const snap: MutationQueueSnapshot = { pending: loadPending(), failed: loadFailed() };
  for (const l of listeners) {
    try { l(snap); } catch { /* isolate listener errors */ }
  }
}

export function subscribeToMutationQueue(listener: Listener): () => void {
  listeners.add(listener);
  try { listener({ pending: loadPending(), failed: loadFailed() }); } catch { /* noop */ }
  return () => { listeners.delete(listener); };
}

// -- Public read accessors --------------------------------------------------

export function getPendingMutations(): QueuedMutation[] { return loadPending(); }
export function getFailedMutations(): FailedMutation[] { return loadFailed(); }
export function isMutationQueueEmpty(): boolean {
  return loadPending().length === 0 && loadFailed().length === 0;
}

// -- Internal mutation helpers ----------------------------------------------

function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function backoffMs(attempts: number): number {
  const exp = Math.min(RETRY_BASE_MS * 2 ** attempts, RETRY_MAX_MS);
  return exp + Math.random() * RETRY_JITTER_MS;
}

function moveToFailed(entry: QueuedMutation, error: string): void {
  const pending = loadPending().filter((e) => e.id !== entry.id);
  savePending(pending);
  const failed = loadFailed();
  failed.push({ ...entry, error, failedAt: new Date().toISOString() });
  saveFailed(failed);
  notify();
}

function bumpEntry(id: string, mut: (e: QueuedMutation) => QueuedMutation): void {
  const pending = loadPending().map((e) => e.id === id ? mut(e) : e);
  savePending(pending);
  notify();
}

// -- Per-id pending promise resolvers ---------------------------------------

interface PendingResolvers {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}
const pendingResolvers = new Map<string, PendingResolvers>();

// -- Core execution ---------------------------------------------------------

async function attemptExecute(entry: QueuedMutation): Promise<void> {
  const exec = executors.get(entry.type);
  if (!exec) {
    moveToFailed(entry, `No executor registered for type "${entry.type}"`);
    pendingResolvers.get(entry.id)?.reject(new Error(`No executor for ${entry.type}`));
    pendingResolvers.delete(entry.id);
    return;
  }

  try {
    const result = await exec(entry.payload);
    // Success — drop from queue, resolve.
    const next = loadPending().filter((e) => e.id !== entry.id);
    savePending(next);
    notify();
    pendingResolvers.get(entry.id)?.resolve(result);
    pendingResolvers.delete(entry.id);
  } catch (err) {
    if (err instanceof PermanentMutationError) {
      moveToFailed(entry, err.message);
      pendingResolvers.get(entry.id)?.reject(err);
      pendingResolvers.delete(entry.id);
      return;
    }

    const attempts = entry.attempts + 1;
    if (attempts >= MAX_ATTEMPTS) {
      const msg = err instanceof Error ? err.message : String(err);
      moveToFailed({ ...entry, attempts }, msg);
      pendingResolvers.get(entry.id)?.reject(err);
      pendingResolvers.delete(entry.id);
      return;
    }

    const nextAttemptAt = Date.now() + backoffMs(attempts);
    bumpEntry(entry.id, (e) => ({ ...e, attempts, nextAttemptAt }));
    scheduleRetry(entry.id, nextAttemptAt - Date.now());
  }
}

const retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
function scheduleRetry(id: string, delayMs: number): void {
  const existing = retryTimers.get(id);
  if (existing) clearTimeout(existing);
  const t = setTimeout(() => {
    retryTimers.delete(id);
    const entry = loadPending().find((e) => e.id === id);
    if (!entry) return; // someone purged it
    void attemptExecute(entry);
  }, Math.max(0, delayMs));
  retryTimers.set(id, t);
}

// -- Public API: enqueue ----------------------------------------------------

export interface EnqueueOptions<P> {
  type: string;
  payload: P;
}

/**
 * Enqueue a mutation and run it. Returns a promise that resolves with the
 * executor's return value once the operation completes (possibly after
 * retries). Rejects only on `PermanentMutationError` or after `MAX_ATTEMPTS`
 * transient failures.
 */
export function enqueueMutation<R = unknown, P = unknown>(opts: EnqueueOptions<P>): Promise<R> {
  const entry: QueuedMutation<P> = {
    id: genId(),
    type: opts.type,
    payload: opts.payload,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    nextAttemptAt: null,
  };
  const pending = loadPending();
  pending.push(entry as QueuedMutation);
  savePending(pending);
  notify();

  return new Promise<R>((resolve, reject) => {
    pendingResolvers.set(entry.id, {
      resolve: (v) => resolve(v as R),
      reject,
    });
    void attemptExecute(entry as QueuedMutation);
  });
}

// -- Drain helpers ----------------------------------------------------------

/**
 * Try every pending entry whose nextAttemptAt has elapsed. Useful on app
 * boot (replay queued mutations from a previous session) and when the
 * browser fires `online`. Caller-driven so tests can run it synchronously.
 */
export function drainMutationQueue(): void {
  const now = Date.now();
  for (const e of loadPending()) {
    const due = e.nextAttemptAt === null || e.nextAttemptAt <= now;
    if (!due) continue;
    if (retryTimers.has(e.id)) continue;
    void attemptExecute(e);
  }
}

/** Dismiss a failed-mutation banner. Caller-driven. */
export function dismissFailedMutation(id: string): void {
  const failed = loadFailed().filter((e) => e.id !== id);
  saveFailed(failed);
  notify();
}

/** Move a failed mutation back into the pending queue with attempts=0. */
export function retryFailedMutation(id: string): void {
  const failed = loadFailed();
  const target = failed.find((e) => e.id === id);
  if (!target) return;
  saveFailed(failed.filter((e) => e.id !== id));
  const next: QueuedMutation = {
    id: target.id,
    type: target.type,
    payload: target.payload,
    queuedAt: target.queuedAt,
    attempts: 0,
    nextAttemptAt: null,
  };
  const pending = loadPending();
  pending.push(next);
  savePending(pending);
  notify();
  void attemptExecute(next);
}

/**
 * Test-only — wipe storage + retry timers + resolvers. Production code
 * shouldn't need this; the queue is meant to survive page reloads.
 */
export function _clearMutationQueueForTests(): void {
  for (const t of retryTimers.values()) clearTimeout(t);
  retryTimers.clear();
  pendingResolvers.clear();
  savePending([]);
  saveFailed([]);
}
